import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ModalService } from '@src/shared/modal/modal.service';
import { scrollTo } from '../../../utils/dom';
import { agencyAdminManagesUser } from '../../../utils/user-permissions';
import { UserRole } from '../../../utils/user-roles';
import { AgencyService } from '../../../woo_services.module/AgencyService';
import { AuthService } from '../../../woo_services.module/AuthService';
import { CustomerService } from '../../../woo_services.module/CustomerService';
import { RoutingService } from '../../../woo_services.module/RoutingService';
import { TranslationService } from '../../../woo_services.module/TranslationService';
import { UserService } from '../../../woo_services.module/UserService';
import { User } from '../../../woo_services.module/shared-types';

@Component({
  selector: 'edit-user',
  templateUrl: './edit-user.component.html',
  standalone: false,
})
export class EditUser implements OnInit {
  readonly availableRoles: ReturnType<EditUser['getAvailableRoles']> = this.getAvailableRoles();
  readonly UserRole = UserRole;

  @ViewChild('editUserSection', { static: true }) editUserSection: ElementRef;
  readonly hasAnyRole;
  readonly hasRole;

  user: User;
  role: { name: UserRole; translatedName: string } = {
    name: null,
    translatedName: '',
  };
  editUser = false;

  cashUserCustomer = null;
  clientUserCustomer = null;
  allAgencies: { id: string; name: string; role: string }[] = [];

  currentUser: User;

  constructor(
    private agencyService: AgencyService,
    private userService: UserService,
    private translationService: TranslationService,
    private customerService: CustomerService,
    private authService: AuthService,
    private modalService: ModalService,
    private route: ActivatedRoute,
    private routingService: RoutingService,
  ) {
    this.currentUser = this.authService.getUser();
    this.hasAnyRole = this.authService.hasAnyRole;
    this.hasRole = this.authService.hasRole;
  }

  async ngOnInit(): Promise<void> {
    // Populate list of all agencies, is used when editing/creating new user
    this.allAgencies = (await this.agencyService.getAgencies()).map((agency) => ({
      id: agency.id,
      name: agency.name,
      role: 'agency_user',
    }));

    await this.refresh();
  }

  userUpdated = async (): Promise<void> => {
    this.editUser = false;
    await this.refresh();
  };

  deactivateUser = async (user: User): Promise<void> => {
    try {
      this.modalService
        .openConfirm({
          title: 'Ta bort användare',
          texts: ['Är du säker på att du vill ta bort den här användaren?'],
          confirmLabel: 'Ta bort användare',
          cancelLabel: 'Avbryt',
        })
        .then(async (response) => {
          if (response.confirm) {
            await this.userService.deactivateUser(user.id);
            if (this.authService.hasRole(UserRole.agencyAdmin)) {
              await this.routingService.navigate(['/accounts']);
              this.modalService.openSuccess(`${user.first_name} ${user.last_name} är nu borttagen!`);
            } else {
              await this.refresh();
              this.editUser = false;
            }
          }
        });
    } catch {
      this.modalService.openError('Kunde inte ta bort användaren. Vänligen kontakta supporten.');
    }
  };

  reactivateUser = async (user: User): Promise<void> => {
    try {
      this.modalService
        .openConfirm({
          title: 'Återaktivera användare',
          texts: ['Är du säker på att du vill återaktivera den här användaren?'],
          confirmLabel: 'Återaktivera användare',
          cancelLabel: 'Avbryt',
        })
        .then(async (response) => {
          if (response.confirm) {
            await this.userService.reactivateUser(user.id);
            this.modalService.openSuccess(`${user.first_name} ${user.last_name} är nu återaktiverad!`);
            await this.refresh();
          }
        });
    } catch {
      this.modalService.openError('Kunde inte återaktivera användaren. Vänligen kontakta supporten!');
    }
  };

  async resetPassword(userId: string): Promise<void> {
    try {
      this.modalService
        .openConfirm({
          title: 'Återställa lösenord för användare',
          texts: ['Detta gör att nästa gång användaren loggar in behöver den sätta om sitt lösenord. Är du säker?'],
          cancelLabel: 'Avbryt',
          confirmLabel: 'Ja, återställ lösenord',
        })
        .then(async (response) => {
          if (response.confirm) {
            await this.userService.resetPassword(userId);
            this.modalService.openSuccess('Nästa gång användaren loggar in behöver den välja ett nytt lösenord.');
          }
        });
    } catch {
      this.modalService.openError('Kunde inte återställa lösenordet. Vänligen kontakta supporten!');
    }
  }

  toggleEdit(): void {
    this.editUser = !this.editUser;
    if (this.editUser) {
      scrollTo(this.editUserSection.nativeElement);
    }
  }

  hasAccountHandlingRole = (): boolean => {
    return this.authService.adminPlannerOrAccounting();
  };

  isAgencyAdmin = function (user: User): number {
    return user.roles.filter((role) => role.name === UserRole.agencyAdmin).length;
  };

  canManageUser = (): boolean => {
    return this.hasRole(UserRole.admin) || agencyAdminManagesUser(this.currentUser, this.user);
  };

  canReactivateAccount = (): boolean => {
    return this.hasRole(UserRole.admin) && !this.user.allowed_to_sign_in && !this.isForgottenUser();
  };

  async refresh(): Promise<void> {
    const user = await this.userService.getUser(this.routingService.getParam(this.route, 'id'));
    this.user = user;

    const firstRole = user.roles.length > 0 ? user.roles[0].name : null;
    let availableRole = this.availableRoles.find((obj) => obj.id === firstRole);

    if ([UserRole.agencyAdmin, UserRole.agencyUser, UserRole.agencyUserAtCustomer].includes(firstRole)) {
      // User is agency_*
      availableRole = { id: UserRole.agency, name: 'Byråanvändare' };
    }

    if (availableRole) {
      this.role.name = availableRole.id;
      this.role.translatedName = availableRole.name;
    } else {
      this.role.name = null;
      this.role.translatedName = '';
    }

    const clientRole = user.roles.find((role) => role.name === UserRole.client);
    const cashRole = user.roles.find((role) => role.name === UserRole.cashUser);
    if (clientRole) {
      this.clientUserCustomer = this.customerService.getCustomer(clientRole.resource_id);
    } else if (cashRole) {
      this.cashUserCustomer = await this.customerService.getCustomer(cashRole.resource_id);
    }
  }

  private getAvailableRoles() {
    return Object.values(UserRole).map((role) => ({ id: role, name: this.translationService.convertRoles(role) }));
  }

  private isForgottenUser = (): boolean => {
    return this.user.first_name === 'Borttagen';
  };
}
